/**
 * Frontend hook that handles tool execution for the Edge AI companion.
 *
 * Security model: Edge AI package emits tool-request events with raw tool calls.
 * This hook validates, snapshots, executes, and manages approve/revert —
 * all within the frontend app boundary. The Edge AI package never directly
 * mutates workspace state.
 */

import { useEffect, useCallback, useRef } from 'react'
import { useCompanionManager, READ_ONLY_TOOLS, toolAlwaysAsks, toolEffect } from '@edge/edge-ai'
import type { ToolCall, ToolResult } from '@edge/edge-ai'
import type { ActionCallbacks, ActionSnapshot } from './types'
import { executeToolBatch } from './tool-executor'
import { NO_RECIPE_MATCH } from './knowledge-messages'
import { describeToolCall, revertSnapshot } from './action-snapshot'
import { useCompanionActionContext } from '@/features/companion/companion-action-hooks'
import {
  createAutomationTurn,
  finishAutomationTurn,
  type AutomationTurn,
} from '@/features/backtest/hooks/graph-automation'
import { assertGraphToolAccess, SAVED_STRATEGY_TOOL_NAMES } from './graph-tools'
import { clearHeadlessExecutionContext } from './headless-execution-context'
import { useAccountStore } from '@/stores/account-store'
import { useApplyWithoutAskingStore } from '@/stores/apply-without-asking-store'
import { useChatStore } from '@/stores/chat-store'

/**
 * What the model hears for a change the trader declined on the card. It names
 * who cancelled and gives the reply's shape: without one, a model barred from
 * "you" blamed the chart ("the chart declined the change", 2026-10-18).
 */
export const DECLINED_CHANGE_MESSAGE =
  'The trader pressed Cancel on the card, so this change was not made and nothing changed. ' +
  'Say so in a few words that name what stays as it was, e.g. "Cancelled. RSI stays on the chart." ' +
  'Never say the app or the chart refused it. Do not try it again unless they ask.'

let proposalCounter = 0

/**
 * Subscribes to tool-request events from the companion manager,
 * executes tools against registered callbacks, and manages the
 * approve/revert lifecycle. Pushes state to CompanionActionContext
 * so the overlay can render approve/revert UI.
 */
export function useCompanionActions(callbacks: ActionCallbacks | null) {
  const manager = useCompanionManager()
  const { setPendingAction, setPendingProposal, setActionHandlers, clearActionHandlers } =
    useCompanionActionContext()
  const snapshotRef = useRef<ActionSnapshot | null>(null)
  const automationRef = useRef<AutomationTurn | null>(null)
  const callbacksRef = useRef(callbacks)
  // Keep callbacks ref current without triggering re-subscription
  callbacksRef.current = callbacks

  const approveAction = useCallback(() => {
    snapshotRef.current = null
    setPendingAction(null)
    manager.events.emit({ type: 'action-resolved', resolution: 'approved' })
  }, [manager, setPendingAction])

  const revertAction = useCallback(() => {
    if (snapshotRef.current && callbacksRef.current) {
      revertSnapshot(snapshotRef.current, callbacksRef.current)
      // The model hears it on the next turn, so it never claims the change.
      manager.noteActionOutcome?.(
        `the trader undid your last change (${snapshotRef.current.description || 'a change'})`,
      )
    }
    snapshotRef.current = null
    setPendingAction(null)
    manager.events.emit({ type: 'action-resolved', resolution: 'reverted' })
  }, [manager, setPendingAction])

  // Register action handlers with context so the overlay can use them
  useEffect(() => {
    setActionHandlers(approveAction, revertAction)
    return () => clearActionHandlers()
  }, [approveAction, revertAction, setActionHandlers, clearActionHandlers])

  // Subscribe to tool-request events from the companion manager
  useEffect(() => {
    const unsub = manager.events.on(async (event) => {
      if (event.type === 'message' && event.message.role === 'assistant' && automationRef.current) {
        finishAutomationTurn(automationRef.current, true)
        clearHeadlessExecutionContext(automationRef.current.id)
      }
      if (event.type === 'error' && automationRef.current) {
        finishAutomationTurn(automationRef.current, false)
        clearHeadlessExecutionContext(automationRef.current.id)
      }
      if (event.type !== 'tool-request') return
      if (!callbacksRef.current) {
        event.resolve(
          event.toolCalls.map((call) => ({
            tool_use_id: call.tool_use_id,
            success: false,
            is_error: true,
            content: 'Page context unavailable; navigate to an active workspace first.',
          })),
        )
        return
      }

      // `event.type` check above narrows the union — toolCalls/resolve are typed.
      const { toolCalls, resolve } = event
      const turnEvent = event as typeof event & { turnId?: string; signal?: AbortSignal }
      if (turnEvent.turnId && turnEvent.signal && automationRef.current?.id !== turnEvent.turnId) {
        if (automationRef.current) clearHeadlessExecutionContext(automationRef.current.id)
        const userId = useAccountStore.getState().session?.user?.sub
        automationRef.current = createAutomationTurn(turnEvent.turnId, turnEvent.signal, () => {
          const account = useAccountStore.getState()
          if (account.session?.user?.sub !== userId)
            throw new Error('Account changed; start a new task.')
          assertGraphToolAccess(false, {
            access: manager.turnAccess,
            authenticated: !!userId,
          })
        })
        turnEvent.signal.addEventListener(
          'abort',
          () => clearHeadlessExecutionContext(turnEvent.turnId),
          { once: true },
        )
      }

      // A change still showing Undo when a new request arrives is KEPT; the new
      // batch takes over the Undo (2026-10-15, one-assistant Phase 2b, Chris).
      // This replaces the old auto-revert (Sec #5), which guarded against an
      // injected change when only Build turns had change tools. That defence
      // is now the turn check: a turn not read as a change request gets no
      // change tools at all (docs/superpowers/plans/2026-10-15-one-assistant-phase-2-plan.md).
      if (snapshotRef.current) {
        approveAction()
      }

      // Ask first (2026-10-15, one-assistant Phase 2c): a call that asks first
      // (toolEffect 'confirm') holds the whole batch until the trader answers
      // the card. "Apply changes without asking" skips the card, except for a
      // change that removes something. Cancel runs the rest of the batch and
      // tells the model the asking-first calls were declined. A stopped turn
      // withdraws the card and runs nothing. A request that carries its own
      // access is the admin portal's Run in app (admin-tool-bridge.ts): the
      // setting is the trader's choice for the AI's calls, so that run always
      // shows the card.
      const calls = toolCalls as ToolCall[]
      const asking = calls.filter((call) => toolEffect(call) === 'confirm')
      const skipCard = !turnEvent.access && useApplyWithoutAskingStore.getState().enabled
      const mustAsk = asking.filter((call) => !skipCard || toolAlwaysAsks(call.name))
      let declined = new Set<string>()
      if (mustAsk.length > 0) {
        const liveCallbacks = callbacksRef.current
        const approved = await new Promise<boolean | 'stopped'>((settle) => {
          let settled = false
          const finish = (answer: boolean | 'stopped') => {
            if (settled) return
            settled = true
            setPendingProposal(null)
            settle(answer)
          }
          if (turnEvent.signal?.aborted) return finish('stopped')
          turnEvent.signal?.addEventListener('abort', () => finish('stopped'), { once: true })
          setPendingProposal({
            id: `proposal_${++proposalCounter}`,
            lines: asking.map((call) => describeToolCall(call, liveCallbacks)),
            apply: () => finish(true),
            cancel: () => finish(false),
          })
          // The conversation is the one place the card shows: open it.
          useChatStore.getState().setOpen(true)
        })
        if (approved === 'stopped') {
          resolve(
            calls.map((call) => ({
              tool_use_id: call.tool_use_id,
              success: false,
              content: 'The turn was stopped before the trader answered; nothing was changed.',
            })),
          )
          return
        }
        if (!approved) declined = new Set(asking.map((call) => call.tool_use_id))
      }
      const runnable = calls.filter((call) => !declined.has(call.tool_use_id))

      // Execute the tool batch. Relay analyst answer tokens live as text-delta
      // events so the drawer types them out (TTFT win) before the final result.
      let outcome: Awaited<ReturnType<typeof executeToolBatch>>
      try {
        outcome = await executeToolBatch(
          runnable,
          callbacksRef.current,
          (text: string) => manager.events.emit({ type: 'text-delta', text, source: 'analyst' }),
          // A request may carry its own access (the admin portal's Run in app
          // sends 'change'); an AI tool call runs with its turn's access.
          turnEvent.access ?? manager.turnAccess,
          automationRef.current ?? undefined,
          () => callbacksRef.current,
        )
      } catch {
        if (automationRef.current) finishAutomationTurn(automationRef.current, false)
        resolve(
          toolCalls.map((call) => ({
            tool_use_id: call.tool_use_id,
            success: false,
            is_error: true,
            content: 'Tool execution stopped because the workspace context changed.',
          })),
        )
        return
      }
      const { snapshot } = outcome
      // One result per call, in the batch's order: declined calls get the
      // decline, the rest their own result.
      const byId = new Map(outcome.results.map((result) => [result.tool_use_id, result]))
      const results: ToolResult[] = calls.map((call) =>
        declined.has(call.tool_use_id)
          ? { tool_use_id: call.tool_use_id, success: false, content: DECLINED_CHANGE_MESSAGE }
          : (byId.get(call.tool_use_id) ?? {
              tool_use_id: call.tool_use_id,
              success: false,
              is_error: true,
              content: 'No result for this call.',
            }),
      )

      // Report results back to the companion manager
      resolve(results)

      // Set up the approve/revert flow. Read-only tools never mutate; graph-edit
      // tools render their own diff card (Keep/Undo) so they opt out of the
      // generic banner. We offer Undo whenever a mutation ACTUALLY APPLIED —
      // including a PARTIAL failure (some ops landed, some failed), which
      // previously left silent changes with no revert affordance (UX-5).
      const noResults = results.every((r: ToolResult) => r.content === NO_RECIPE_MATCH)
      const CARD_PRODUCING_TOOLS = new Set([
        'graph_call', // graph edits have cards; lifecycle/compute have irreversible receipts
        'add_node',
        'connect_nodes',
        'set_node_parameters',
        'remove_node',
        'rerun_backtest',
        'apply_graph_ops',
        'create_strategy_from_recipe',
        'create_strategy_template',
      ])
      // Saved-strategy tools get no banner, except an edit: its Undo writes the
      // earlier graph back (2026-10-15).
      const noBanner = (name: string) =>
        READ_ONLY_TOOLS.has(name) ||
        (SAVED_STRATEGY_TOOL_NAMES.has(name) && name !== 'edit_saved_strategy')
      const mutations = (toolCalls as ToolCall[]).filter((tc) => !noBanner(tc.name))
      const allCardProducing =
        mutations.length > 0 && mutations.every((tc) => CARD_PRODUCING_TOOLS.has(tc.name))
      // A mutation whose result succeeded → something was applied to the workspace.
      const anyMutationApplied = (toolCalls as ToolCall[]).some(
        (tc, i) => !noBanner(tc.name) && results[i]?.success,
      )

      if (anyMutationApplied && !allCardProducing && !noResults) {
        // Flag partial failures so the banner tells the user some steps failed.
        snapshot.partial = !results.every((r: ToolResult) => r.success)
        snapshotRef.current = snapshot
        setPendingAction(snapshot)
        manager.events.emit({ type: 'action-pending', snapshot })
      }
    })

    return () => {
      unsub()
      if (automationRef.current) {
        finishAutomationTurn(automationRef.current, false)
        clearHeadlessExecutionContext(automationRef.current.id)
      }
    }
  }, [manager, approveAction, setPendingAction, setPendingProposal])
}
